import { useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@paulownia/shared/components/ui/dialog';
import { Button } from '@paulownia/shared/components/ui/button';
import { Input } from '@paulownia/shared/components/ui/input';
import { Label } from '@paulownia/shared/components/ui/label';
import { TimePicker } from '@paulownia/shared/components/TimePicker';
import { type RecurringOrder, type RecurringOrderLine, type Product } from '@paulownia/shared/types';
import { updateRecurringOrder } from '@paulownia/shared/services/recurring-orders';

interface Props {
    order: RecurringOrder;
    products: Product[];
    onSave: (updates: Parameters<typeof updateRecurringOrder>[1]) => Promise<void>;
    onClose: () => void;
}

export function RecurringOrderEditDialog({ order, products, onSave, onClose }: Props) {
    const [customerName, setCustomerName] = useState(order.customerName);
    const [phoneNumber, setPhoneNumber] = useState(order.phoneNumber ?? '');
    const [preferredPickupTime, setPreferredPickupTime] = useState(order.preferredPickupTime);
    const [note, setNote] = useState(order.note ?? '');
    const [lines, setLines] = useState<RecurringOrderLine[]>(order.lines);
    const [saving, setSaving] = useState(false);

    const getProductName = (productId: string) =>
        products.find(p => p.id === productId)?.name ?? productId;

    const updateLineQuantity = (index: number, quantity: number) => {
        setLines(prev => prev.map((l, i) => i === index ? { ...l, quantity: Math.max(1, quantity) } : l));
    };

    const removeLine = (index: number) => {
        setLines(prev => prev.filter((_, i) => i !== index));
    };

    const handleSave = async () => {
        if (!customerName.trim()) return;
        if (lines.length === 0) return;
        try {
            setSaving(true);
            await onSave({
                customerName: customerName.trim(),
                phoneNumber: phoneNumber.trim() || undefined,
                preferredPickupTime,
                note: note.trim() || undefined,
                lines,
            });
        } finally {
            setSaving(false);
        }
    };

    return (
        <Dialog open onOpenChange={onClose}>
            <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
                <DialogHeader>
                    <DialogTitle>Modifier la commande récurrente</DialogTitle>
                </DialogHeader>

                <div className="space-y-4 py-2">
                    <div className="space-y-2">
                        <Label>Nom du client</Label>
                        <Input
                            value={customerName}
                            onChange={e => setCustomerName(e.target.value)}
                            placeholder="Nom du client"
                        />
                    </div>

                    <div className="space-y-2">
                        <Label>Téléphone (optionnel)</Label>
                        <Input
                            value={phoneNumber}
                            onChange={e => setPhoneNumber(e.target.value.replace(/\D/g, ''))}
                            placeholder="0612345678"
                            inputMode="tel"
                        />
                    </div>

                    <TimePicker
                        label="Heure de retrait"
                        value={preferredPickupTime}
                        onChange={setPreferredPickupTime}
                    />

                    <div className="space-y-2">
                        <Label>Produits</Label>
                        <div className="space-y-2">
                            {lines.map((line, idx) => (
                                <div key={idx} className="flex items-center gap-3 rounded-lg border bg-gray-50 p-3">
                                    <span className="flex-1 text-sm font-medium text-gray-900">
                                        {getProductName(line.productId)}
                                    </span>
                                    <div className="flex items-center gap-2">
                                        <Button
                                            variant="outline"
                                            size="icon"
                                            className="h-7 w-7"
                                            onClick={() => updateLineQuantity(idx, line.quantity - 1)}
                                        >
                                            −
                                        </Button>
                                        <span className="w-6 text-center font-semibold">{line.quantity}</span>
                                        <Button
                                            variant="outline"
                                            size="icon"
                                            className="h-7 w-7"
                                            onClick={() => updateLineQuantity(idx, line.quantity + 1)}
                                        >
                                            +
                                        </Button>
                                        <Button
                                            variant="ghost"
                                            size="icon"
                                            className="h-7 w-7 text-red-500 hover:text-red-600"
                                            onClick={() => removeLine(idx)}
                                        >
                                            ×
                                        </Button>
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>

                    <div className="space-y-2">
                        <Label>Note (optionnel)</Label>
                        <textarea
                            value={note}
                            onChange={e => setNote(e.target.value)}
                            placeholder="Note personnalisée..."
                            className="w-full min-h-[72px] px-3 py-2 border border-gray-300 rounded-md text-sm resize-none focus:outline-none focus:ring-2 focus:ring-orange-500"
                        />
                    </div>

                    <div className="flex gap-2 pt-2">
                        <Button variant="outline" className="flex-1" onClick={onClose}>
                            Annuler
                        </Button>
                        <Button
                            className="flex-1 bg-orange-600 hover:bg-orange-700"
                            onClick={handleSave}
                            disabled={saving || !customerName.trim() || lines.length === 0}
                        >
                            {saving ? 'Enregistrement...' : 'Enregistrer'}
                        </Button>
                    </div>
                </div>
            </DialogContent>
        </Dialog>
    );
}
